import React from 'react';
import './StoreDetailModal.css';

const StoreDetailModal = ({
  type,
  item,
  data,
  formatCurrency,
  formatDate,
  onClose,
}) => {
  if (!item) return null;

  const orders = data?.orders || [];
  const products = data?.products || [];
  const customers = data?.customers || [];

  const renderProductContent = () => {
    const productOrders = orders.filter((o) => {
      const matchId = o.productId === item.productId || o.productId === item.$id;
      const matchName = (o.productName || '').toLowerCase() === (item.productName || '').toLowerCase();
      return matchId || matchName;
    });
    const unitsSold = productOrders.reduce((sum, o) => sum + (parseInt(o.quantity, 10) || 0), 0);
    const revenue = productOrders.reduce((sum, o) => sum + (parseFloat(o.totalAmount) || 0), 0);
    const orderCount = productOrders.length;

    return (
      <>
        <div className="store-detail-modal-section">
          <h3>Product Details</h3>
          <div className="store-detail-modal-grid">
            <div className="store-detail-modal-field">
              <label>Product Name</label>
              <span>{item.productName || '—'}</span>
            </div>
            <div className="store-detail-modal-field">
              <label>Category</label>
              <span>{item.productCategory || '—'}</span>
            </div>
            <div className="store-detail-modal-field">
              <label>Price</label>
              <span>{formatCurrency(item.sellPrice)} TZS</span>
            </div>
            <div className="store-detail-modal-field">
              <label>Stock</label>
              <span>{item.stockCount ?? '—'}</span>
            </div>
            <div className="store-detail-modal-field">
              <label>Unit</label>
              <span>{item.unit || '—'}</span>
            </div>
            <div className="store-detail-modal-field">
              <label>Product ID</label>
              <span className="store-detail-modal-id">{item.$id || '—'}</span>
            </div>
          </div>
        </div>
        <div className="store-detail-modal-section">
          <h3>Sales Analytics</h3>
          <div className="store-detail-modal-stats">
            <div className="store-detail-modal-stat">
              <span className="store-detail-modal-stat-value">{unitsSold}</span>
              <span className="store-detail-modal-stat-label">Units Sold</span>
            </div>
            <div className="store-detail-modal-stat">
              <span className="store-detail-modal-stat-value">{formatCurrency(revenue)}</span>
              <span className="store-detail-modal-stat-label">Revenue</span>
            </div>
            <div className="store-detail-modal-stat">
              <span className="store-detail-modal-stat-value">{orderCount}</span>
              <span className="store-detail-modal-stat-label">Orders</span>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderOrderContent = () => {
    const customer = item.customerId
      ? customers.find((c) => (c.$id || c.uid) === item.customerId)
      : null;
    const customerName = customer
      ? [customer.fname, customer.lname].filter(Boolean).join(' ') || customer.email || 'Unknown'
      : '—';

    return (
      <>
        <div className="store-detail-modal-section">
          <h3>Order Details</h3>
          <div className="store-detail-modal-grid">
            <div className="store-detail-modal-field">
              <label>Order ID</label>
              <span>{item.orderId || item.$id || '—'}</span>
            </div>
            <div className="store-detail-modal-field">
              <label>Date</label>
              <span>{formatDate(item.orderDate || item.$createdAt)}</span>
            </div>
            <div className="store-detail-modal-field">
              <label>Product</label>
              <span>{item.productName || '—'}</span>
            </div>
            <div className="store-detail-modal-field">
              <label>Quantity</label>
              <span>{item.quantity ?? '—'}</span>
            </div>
            <div className="store-detail-modal-field">
              <label>Amount</label>
              <span className="store-detail-modal-amount">{formatCurrency(item.totalAmount)} TZS</span>
            </div>
            <div className="store-detail-modal-field">
              <label>Status</label>
              <span>
                <span className={`store-detail-status store-detail-status-${(item.status || '').toLowerCase()}`}>
                  {item.status || '—'}
                </span>
              </span>
            </div>
            <div className="store-detail-modal-field store-detail-modal-field-full">
              <label>Customer</label>
              <span>{customerName}</span>
            </div>
            {customer?.email && (
              <div className="store-detail-modal-field store-detail-modal-field-full">
                <label>Customer Email</label>
                <span>
                  <a href={`mailto:${customer.email}`}>{customer.email}</a>
                </span>
              </div>
            )}
            {customer?.phoneNumber && (
              <div className="store-detail-modal-field">
                <label>Customer Phone</label>
                <span>
                  <a href={`tel:${customer.phoneNumber}`}>{customer.phoneNumber}</a>
                </span>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const renderCustomerContent = () => {
    const customerOrders = orders.filter((o) => o.customerId === (item.$id || item.uid));
    const totalSpent = customerOrders.reduce((sum, o) => sum + (parseFloat(o.totalAmount) || 0), 0);
    const orderCount = customerOrders.length;

    return (
      <>
        <div className="store-detail-modal-section">
          <h3>Customer Details</h3>
          <div className="store-detail-modal-grid">
            <div className="store-detail-modal-field">
              <label>Name</label>
              <span>{[item.fname, item.lname].filter(Boolean).join(' ') || '—'}</span>
            </div>
            <div className="store-detail-modal-field store-detail-modal-field-full">
              <label>Email</label>
              <span>
                {item.email ? (
                  <a href={`mailto:${item.email}`}>{item.email}</a>
                ) : (
                  '—'
                )}
              </span>
            </div>
            <div className="store-detail-modal-field">
              <label>Phone</label>
              <span>
                {item.phoneNumber ? (
                  <a href={`tel:${item.phoneNumber}`}>{item.phoneNumber}</a>
                ) : (
                  '—'
                )}
              </span>
            </div>
            <div className="store-detail-modal-field">
              <label>Customer ID</label>
              <span className="store-detail-modal-id">{item.$id || item.uid || '—'}</span>
            </div>
          </div>
        </div>
        <div className="store-detail-modal-section">
          <h3>Purchase Analytics</h3>
          <div className="store-detail-modal-stats">
            <div className="store-detail-modal-stat">
              <span className="store-detail-modal-stat-value">{orderCount}</span>
              <span className="store-detail-modal-stat-label">Total Orders</span>
            </div>
            <div className="store-detail-modal-stat">
              <span className="store-detail-modal-stat-value">{formatCurrency(totalSpent)}</span>
              <span className="store-detail-modal-stat-label">Total Spent</span>
            </div>
            <div className="store-detail-modal-stat">
              <span className="store-detail-modal-stat-value">
                {orderCount > 0 ? formatCurrency(totalSpent / orderCount) : '—'}
              </span>
              <span className="store-detail-modal-stat-label">Avg Order Value</span>
            </div>
          </div>
        </div>
        {customerOrders.length > 0 && (
          <div className="store-detail-modal-section">
            <h3>Recent Orders</h3>
            <div className="store-detail-modal-table-wrap">
              <table className="store-detail-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Date</th>
                    <th>Product</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {customerOrders.slice(0, 10).map((o) => (
                    <tr key={o.$id}>
                      <td>{o.orderId || o.$id}</td>
                      <td>{formatDate(o.orderDate)}</td>
                      <td>{o.productName || '—'}</td>
                      <td>{formatCurrency(o.totalAmount)}</td>
                      <td>
                        <span className={`store-detail-status store-detail-status-${(o.status || '').toLowerCase()}`}>
                          {o.status || '—'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {customerOrders.length > 10 && (
                <p className="store-detail-modal-more">
                  +{customerOrders.length - 10} more orders
                </p>
              )}
            </div>
          </div>
        )}
      </>
    );
  };

  const titles = {
    product: item.productName || 'Product',
    order: `Order ${item.orderId || item.$id}`,
    customer: [item.fname, item.lname].filter(Boolean).join(' ') || item.email || 'Customer',
  };

  let body;
  if (type === 'product') body = renderProductContent();
  else if (type === 'order') body = renderOrderContent();
  else if (type === 'customer') body = renderCustomerContent();

  return (
    <div className="store-detail-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div
        className="store-detail-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="store-detail-modal-header">
          <h2>{titles[type] || 'Details'}</h2>
          <button
            type="button"
            className="store-detail-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="store-detail-modal-body">
          {body}
        </div>
      </div>
    </div>
  );
};

export default StoreDetailModal;
